import 'react-phone-input-2/lib/style.css';

import {
  Flex,
  HStack,
  Link,
  PinInput,
  PinInputField,
  Spinner,
  Text,
  Input,
  Button,
  VStack,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { useEffect, useState } from 'react';

import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';

export function VerifyContact() {
  const {
    contactId,
    contactModalError,
    setShowPricingModal,
    verifyContact,
    toggleContactModal,
    user,
    registerContact,
    currentPage,
    code,
    setContactStatus,
    hasEmail,
    email,
    setEmail,
  } = useUIStore(
    (state) => ({
      contactId: state.contactInput.contactId,
      contactModalError: state.contactModalError,
      setShowPricingModal: state.setShowPricingModal,
      verifyContact: state.verifyContact,
      toggleContactModal: state.toggleContactModal,
      user: state.user,
      registerContact: state.registerContact,
      currentPage: state.currentPage,
      code: state.code,
      setContactStatus: state.setContactStatus,
      hasEmail: state.hasEmail,
      email: state.email,
      setEmail: state.setEmail,
    }),
    shallow
  );

  const [seconds, setSeconds] = useState(0);
  const [parsedPhoneNumber, setParsedPhoneNumber] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  
  // Check if it's a US/CA number (country code +1)
  const isUSorCA = contactId?.startsWith('1');
  const minutes = Math.floor(seconds / 60);
  const secondsToDisplay = seconds - minutes * 60;

  const parseInputPhoneNumber = async () => {
    const { parsePhoneNumber } = await import('libphonenumber-js');
    const libParsedPhoneNumber = parsePhoneNumber(
      `+${contactId}`
    ).formatInternational();
    setParsedPhoneNumber(libParsedPhoneNumber);
  };
  const handleEdit = () => {
    // Reset OTP value when editing
    setOtpValue('');
    // Set the status to register
    setContactStatus('register');
  };

  useEffect(() => {
    // For displaying the number
    parseInputPhoneNumber();
    resetTimer();
  }, [contactId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [seconds]);

  const resetTimer = () => {
    setSeconds(30);
  };

  const handleVerify = async (otp = otpValue) => {
    setLoading(true);
    await verifyContact({ otp });
    setLoading(false);
  };

  const handleOtpComplete = (value) => {
    if (hasEmail) {
      handleVerify(value);
    }
  };

  return (
    <VStack spacing={6} align="stretch" w="100%" maxW="400px" mx="auto" px={4}>
      {/* Header Section */}
      <VStack spacing={3} align="center">
        <Text
          fontSize="lg"
          fontWeight="semibold"
          color="white"
          textAlign="center"
          lineHeight="1.5"
        >
          Enter {isUSorCA ? '' : 'WhatsApp'} OTP sent to{' '}
          <Text as="span" whiteSpace="nowrap" fontWeight="bold">
            {parsedPhoneNumber}
          </Text>
        </Text>
        <Text
          fontSize="sm"
          color="white"
          textDecor="underline"
          cursor="pointer"
          _hover={{ color: 'brand.100' }}
          onClick={handleEdit}
        >
          Doesn't look right? Edit number
        </Text>
      </VStack>

      {/* OTP Input Section */}
      <VStack spacing={5} align="stretch">
         <HStack position="relative" justify="center" spacing={3}>
           <PinInput
             otp
             value={otpValue}
             onChange={setOtpValue}
             onComplete={handleOtpComplete}
             size="lg"
             colorScheme="white"
             focusBorderColor="white"
             placeholder=""
           >
            <PinInputField
              color="white"
              borderColor="white"
              borderWidth="1px"
              _hover={{ borderColor: 'brand.300' }}
              _focus={{ borderColor: 'brand.400', bg: 'whiteAlpha.100' }}
              fontSize="xl"
              fontWeight="bold"
            />
            <PinInputField
              color="white"
              borderColor="white"
              borderWidth="1px"
              _hover={{ borderColor: 'brand.300' }}
              _focus={{ borderColor: 'brand.400', bg: 'whiteAlpha.100' }}
              fontSize="xl"
              fontWeight="bold"
            />
            <PinInputField
              color="white"
              borderColor="white"
              borderWidth="1px"
              _hover={{ borderColor: 'brand.300' }}
              _focus={{ borderColor: 'brand.400', bg: 'whiteAlpha.100' }}
              fontSize="xl"
              fontWeight="bold"
            />
            <PinInputField
              color="white"
              borderColor="white"
              borderWidth="1px"
              _hover={{ borderColor: 'brand.300' }}
              _focus={{ borderColor: 'brand.400', bg: 'whiteAlpha.100' }}
              fontSize="xl"
              fontWeight="bold"
            />
          </PinInput>
          {isLoading && (
            <Spinner
              position="absolute"
              right={-8}
              color="white"
              size="sm"
            />
          )}
        </HStack>

        {/* Email Input */}
        {!hasEmail && (
          <Input
            placeholder="Email"
            type="email"
            value={email || ''}
            onChange={(e) => setEmail(e.target.value)}
            size="md"
            color="white"
            borderColor="white"
            borderWidth="1px"
            _placeholder={{ color: 'whiteAlpha.800' }}
            _hover={{ borderColor: 'brand.300' }}
            _focus={{ borderColor: 'brand.400', bg: 'whiteAlpha.100' }}
            bg="transparent"
          />
        )}

        {/* Verify Button - Only show for new phone numbers */}
        {!hasEmail && (
          <Button
            onClick={() => handleVerify()}
            isDisabled={otpValue.length !== 4 || isLoading}
            isLoading={isLoading}
            size="lg"
            colorScheme="white"
            color="black"
            bg="white"
            fontWeight="semibold"
            _hover={{
              opacity: otpValue.length === 4 ? 0.9 : 0.7,
              transform: otpValue.length === 4 ? 'translateY(-1px)' : 'none',
            }}
            _active={{ transform: 'translateY(0px)' }}
            opacity={otpValue.length === 4 ? 1 : 0.7}
            transition="all 0.2s"
          >
            Verify
          </Button>
        )}
      </VStack>

      {/* Error Message */}
      {contactModalError && (
        <Flex
          align="center"
          justify="center"
          bg="brand.500"
          color="white"
          px={4}
          py={2}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
        >
          <WarningIcon mr={2} />
          <Text>{contactModalError}</Text>
        </Flex>
      )}

      {/* Timer/Resend Section */}
      <Flex justify="center" mt={2}>
        {seconds > 0 ? (
          <Text fontSize="sm" color="white" fontWeight="medium">
            Time Remaining: {minutes < 10 ? `0${minutes}` : minutes}:
            {secondsToDisplay < 10 ? `0${secondsToDisplay}` : secondsToDisplay}
          </Text>
        ) : (
          <Text fontSize="sm" color="white">
            Didn&apos;t get the OTP{isUSorCA ? '' : ' on WhatsApp'}?{' '}
            <Link
              onClick={() => {
                setOtpValue('');
                resetTimer();
                registerContact();
              }}
              color="brand.200"
              textDecor="underline"
              fontWeight="semibold"
              _hover={{ color: 'brand.100' }}
              to=""
            >
              Resend
            </Link>
          </Text>
        )}
      </Flex>
    </VStack>
  );
}
